import { useState } from "react";
import GarlandDecorationSection from "./GarlandDecorationSection";

const Match = ({ 
    searchTerm,
    setSearchTerm,
    filteredSubmissions,
    findMatches,
    showForm,
    newUserAdded,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [hasAdvanced, setHasAdvanced] = useState(false); // Track if the user clicked "Show More"
  const pageSize = 6; // Number of cards per page

  const showMore = () => {
    setCurrentPage((prevPage) => prevPage + 1); // Move to the next page
    setHasAdvanced(true); // Mark that the user has advanced
  };

  const showPrevious = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 0)); // Move to the previous page
    if (currentPage - 1 === 0) {
      setHasAdvanced(false); // Reset if returning to the first page
    }
  };

  // Get the subset of submissions for the current page
  const currentSubmissions = filteredSubmissions.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  // Function to check if the submission is the best match
  const isBestMatch = (submission) => {
    const lastUser = filteredSubmissions[filteredSubmissions.length - 1];
    if (!lastUser) return false;

    const lastUserWords = [
      lastUser.achievement,
      lastUser.expertise,
      lastUser.support,
    ]
      .join(" ")
      .toLowerCase()
      .split(/\s+/);

    const submissionFields = [
      submission.achievement,
      submission.expertise,
      submission.support,
    ]
      .join(" ")
      .toLowerCase();

    return lastUserWords.some((word) => submissionFields.includes(word));
  };

  return (
    <div id="match-section" className="bg-gradient-to-b rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-2xl font-cormorant text-green-800 text-center mb-6">
          ✨ Find Your Match ✨
        </h2>
        {/* Garland Decoration */}
        <GarlandDecorationSection />
        <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
          <input
            placeholder="Search for expertise or needs (e.g., 'grant writing' or 'marketing')"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 border border-green-200 rounded"
          />
        </div>
        {showForm && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="mb-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-cormorant text-green-800">
                      {submission.name}
                    </h3>
                    <a
                      href={submission.linkedinURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="linkedin-link"
                    >
                      Connect on{" "}
                      <img
                        className="image-linkedin"
                        alt="LinkedIn logo"
                        srcset="https://content.linkedin.com/content/dam/me/business/en-us/amp/brand-site/v2/bg/LI-Logo.svg.original.svg"
                      />
                    </a>
                  </div>
                  <p className="text-gray-600 text-sm">{submission.achievement}</p>
                </div>

                <div className="mb-2">
                  <p className="font-medium text-green-700">Can offer:</p>
                  <p className="text-green-600 ml-2">{submission.expertise}</p>
                </div>

                <div className="mb-2">
                  <p className="font-medium text-green-700">Seeking:</p>
                  <p className="text-green-600 ml-2">{submission.support}</p>
                </div>

                {/* Conditional Best Match Block */}
                {isBestMatch(submission) &&
                  submission !== filteredSubmissions[filteredSubmissions.length - 1] &&
                  newUserAdded && (
                    <div className="mb-2 best-match">
                      <p className="font-large text-green-700">⭐ Best Match ⭐</p>
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-6 text-center show-more-block pb-4">
        {hasAdvanced && (
          <button
            onClick={showPrevious}
            className="px-4 py-2 bg-gray-500 text-white rounded shadow hover:bg-gray-600 transition-colors"
          >
            Show Previous
          </button>
        )}
        {(currentPage + 1) * pageSize < filteredSubmissions.length && (
          <button
            onClick={showMore}
            className={`${
              hasAdvanced ? "ml-4" : ""
            } px-4 py-2 bg-green-500 text-white rounded shadow hover:bg-green-600 transition-colors`}
          >
            Show More
          </button>
        )}
      </div>
    </div>
  );
};

export default Match;
